import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface ObjectiveRow {
  readonly objectiveId: string;
  readonly objectiveCode: string;
  readonly serviceName: string;
  readonly criticalityTier: string;
  readonly rtoMinutes: number;
  readonly rpoMinutes: number;
  readonly actualRtoMinutes: number | null;
  readonly actualRpoMinutes: number | null;
  readonly objectiveStatus: string;
}

const TIERS = ['', 'TIER_1', 'TIER_2', 'TIER_3'];
const STATUSES = ['', 'DEFINED', 'APPROVED', 'MET', 'BREACHED'];
const CREATE_TIERS = TIERS.filter(Boolean);

/**
 * M31 — Recovery Objective Dashboard (§9 / §31). Per-service RTO/RPO targets vs
 * achieved actuals with the objective lifecycle status (DEFINED → APPROVED →
 * MET / BREACHED). LAW 6: default targets come from tenant params per tier.
 * Supports defining a new objective, approving it and recording actual RTO/RPO —
 * each wired to the guarded /v1/bcm/recovery-objectives write API. Filterable by
 * criticality tier and status.
 */
export function RecoveryObjectivePage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<ObjectiveRow[]>([]);
  const [criticalityTier, setCriticalityTier] = useState('');
  const [objectiveStatus, setObjectiveStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [newService, setNewService] = useState('');
  const [newTier, setNewTier] = useState('TIER_1');
  const [actualRto, setActualRto] = useState<Record<string, string>>({});
  const [actualRpo, setActualRpo] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (criticalityTier) params.set('criticalityTier', criticalityTier);
    if (objectiveStatus) params.set('objectiveStatus', objectiveStatus);
    const qs = params.toString();
    apiRequest<{ data: ObjectiveRow[] }>(`/v1/bcm/recovery-objectives${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, criticalityTier, objectiveStatus, reload]);

  const failMessage = (err: unknown): string =>
    err instanceof ApiError && err.status === 403
      ? tr('m31.objective.err403')
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newService.trim()) { setActionError(tr('m31.objective.errService')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/bcm/recovery-objectives`, {
        method: 'POST',
        body: { serviceName: newService.trim(), criticalityTier: newTier },
        token,
      });
      setNotice(tr('m31.objective.noticeDefined', { service: newService.trim() }));
      setNewService('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: ObjectiveRow, path: string, label: string, body?: unknown): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/bcm/recovery-objectives/${r.objectiveId}/${path}`, { method: 'POST', body: body ?? {}, token });
      setNotice(tr('m31.objective.actNotice', { code: r.objectiveCode, label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    }
  };

  const recordActuals = async (r: ObjectiveRow): Promise<void> => {
    const rto = (actualRto[r.objectiveId] ?? '').trim();
    const rpo = (actualRpo[r.objectiveId] ?? '').trim();
    if (!rto || !rpo) { setActionError(tr('m31.objective.errActuals')); return; }
    await act(r, 'actuals', tr('m31.objective.actualsRecorded'), { actualRtoMinutes: Number(rto), actualRpoMinutes: Number(rpo) });
  };

  return (
    <main aria-label="recovery-objectives">
      <h1>{tr('m31.objective.title')}</h1>

      <section aria-label="define-objective">
        <h2>{tr('m31.objective.defineHeading')}</h2>
        <form aria-label="objective-form" onSubmit={(e) => void create(e)}>
          <label>{tr('m31.objective.fieldService')}<input aria-label="field-service-name" value={newService} onChange={(e) => setNewService(e.target.value)} /></label>
          <label>{tr('m31.objective.fieldTier')}
            <select aria-label="field-tier" value={newTier} onChange={(e) => setNewTier(e.target.value)}>
              {CREATE_TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <button type="submit" aria-label="submit-objective" disabled={busy}>{busy ? tr('m31.objective.busyDefine') : tr('m31.objective.submitDefine')}</button>
        </form>
        {actionError && <p role="alert" aria-label="objective-error">{actionError}</p>}
        {notice && <p aria-label="objective-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {tr('m31.objective.filterTier')}
          <select aria-label="filter-tier" value={criticalityTier} onChange={(e) => setCriticalityTier(e.target.value)}>
            {TIERS.map((t) => <option key={t || 'all'} value={t}>{t || tr('m31.objective.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('m31.objective.filterStatus')}
          <select aria-label="filter-status" value={objectiveStatus} onChange={(e) => setObjectiveStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || tr('m31.objective.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m31.objective.loading')}</p> : (
        <>
          <p aria-label="objective-count">{tr('m31.objective.count', { count: rows.length })}</p>
          <table aria-label="objective-table">
            <thead><tr><th>{tr('m31.objective.thCode')}</th><th>{tr('m31.objective.thService')}</th><th>{tr('m31.objective.thTier')}</th><th>{tr('m31.objective.thRto')}</th><th>{tr('m31.objective.thRpo')}</th><th>{tr('m31.objective.thActualRto')}</th><th>{tr('m31.objective.thActualRpo')}</th><th>{tr('m31.objective.thStatus')}</th><th>{tr('m31.objective.thActions')}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.objectiveId} data-status={r.objectiveStatus}>
                  <td>{r.objectiveCode}</td><td>{r.serviceName}</td><td>{r.criticalityTier}</td>
                  <td>{r.rtoMinutes}m</td><td>{r.rpoMinutes}m</td>
                  <td>{r.actualRtoMinutes === null ? '—' : `${r.actualRtoMinutes}m`}</td>
                  <td>{r.actualRpoMinutes === null ? '—' : `${r.actualRpoMinutes}m`}</td>
                  <td>{r.objectiveStatus}</td>
                  <td>
                    {r.objectiveStatus === 'DEFINED' && (
                      <button type="button" aria-label={`approve-${r.objectiveCode}`} onClick={() => void act(r, 'approve', tr('m31.objective.approvedLabel'))}>{tr('m31.objective.approveBtn')}</button>
                    )}
                    {r.objectiveStatus !== 'DEFINED' && (
                      <>
                        <input aria-label={`actual-rto-${r.objectiveCode}`} type="number" placeholder={tr('m31.objective.actualRtoPlaceholder')} value={actualRto[r.objectiveId] ?? ''} onChange={(e) => setActualRto((m) => ({ ...m, [r.objectiveId]: e.target.value }))} />
                        <input aria-label={`actual-rpo-${r.objectiveCode}`} type="number" placeholder={tr('m31.objective.actualRpoPlaceholder')} value={actualRpo[r.objectiveId] ?? ''} onChange={(e) => setActualRpo((m) => ({ ...m, [r.objectiveId]: e.target.value }))} />
                        <button type="button" aria-label={`actuals-${r.objectiveCode}`} onClick={() => void recordActuals(r)}>{tr('m31.objective.recordActuals')}</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
